/* eslint-disable @typescript-eslint/no-explicit-any */
import { SWAN_STYLE_KEY_MAP } from '@vp/swan'
import { PillarSlimBanner } from './PillarSlimBanner/PillarSlimBanner'
import { PromoSlimBanner } from './PromoSlimBanner/PromoSlimBanner'
import { SpacingWrapper } from './SpacingWrapper/SpacingWrapper'
import { useLogger, useSwanStyleKeys, useUserContextTracker } from '@vp/ubik-context'
import type { FragmentLogger } from '@vp/ubik-logging'
import { ContentfulContextProvider, ContentfulLink, DebugSection } from '@vp/mcs-react-tools'

export const Fragment = (props: any) => {
  useSwanStyleKeys([SWAN_STYLE_KEY_MAP.core])

  const logger = useLogger() as FragmentLogger

  logger.setContext({
    entry: props.contentfulMetadata?.contentful_id,
    name: 'slim-banner',
    'vista.team_tag': 'VPD-0001',
  })

  logger.info('Rendering Slim Banner Fragment')

  trackUsedContext(props)

  if (!props.pageData) {
    logger.warn('No page data found, returning null fragment')
    return <SpacingWrapper />
  }
  const bannerType = props.pageData.slimBannerType ?? false
  const promoData = {
    promoHeading: props.pageData.promoHeadingEntry || props.pageData.promoHeading || null,
    promoCode: props.pageData.promoCodeEntry || props.pageData.promoCode || null,
    promoLegalText: props.pageData.promoLegalTextEntry || props.pageData.promoLegalText || null,
    promoLegalLink: {
      text: props.pageData?.promoLegalLink?.text || null,
      targetUrl: props.pageData?.promoLegalLink?.targetUrl?.contentfulUrl || null,
    },
    promoEndDate: props.pageData.promoEndDateEntry || props.pageData.promoEndDate || null,
    promoFirstCta: {
      text: props.pageData?.promoFirstCta?.text || null,
      targetUrl: props.pageData?.promoFirstCta?.targetUrl?.contentfulUrl || null,
    },
    promoSecondCta: {
      text: props.pageData?.promoSecondCta?.text || null,
      targetUrl: props.pageData?.promoSecondCta?.targetUrl?.contentfulUrl || null,
    },
    backgroundColor: props.pageData.backgroundColor || null,
  }
  const pillarData = [
    {
      icon: props.pageData.pillar1Icon || null,
      heading: props.pageData.pillar1HeadingEntry || props.pageData.pillar1Heading || null,
      description: props.pageData.pillar1DescriptionEntry || props.pageData.pillar1Description || null,
    },
    {
      icon: props.pageData.pillar2Icon || null,
      heading: props.pageData.pillar2HeadingEntry || props.pageData.pillar2Heading || null,
      description: props.pageData.pillar2DescriptionEntry || props.pageData.pillar2Description || null,
    },
    {
      icon: props.pageData.pillar3Icon || null,
      heading: props.pageData.pillar3HeadingEntry || props.pageData.pillar3Heading || null,
      description: props.pageData.pillar3DescriptionEntry || props.pageData.pillar3Description || null,
    },
  ]

  const entryToTextFieldMap: Record<string, string> = {
    promoHeadingEntry: 'promoHeading',
    promoCodeEntry: 'promoCode',
    promoLegalTextEntry: 'promoLegalText',
    promoEndDateEntry: 'promoEndDate',
    pillar1HeadingEntry: 'pillar1Heading',
    pillar1DescriptionEntry: 'pillar1Description',
    pillar2HeadingEntry: 'pillar2Heading',
    pillar2DescriptionEntry: 'pillar2Description',
    pillar3HeadingEntry: 'pillar3Heading',
    pillar3DescriptionEntry: 'pillar3Description',
  }

  const usedTextFields: string[] = []

  Object.entries(entryToTextFieldMap).forEach(([entryKey, textFieldKey]) => {
    if (!props.pageData[entryKey] && props.pageData[textFieldKey]) {
      usedTextFields.push(textFieldKey)
    }
  })

  if (usedTextFields.length > 0) {
    logger.warn(`Using plain text properties for: ${usedTextFields.join(', ')}`)
  }

  const search = typeof window !== 'undefined' ? window.location.search : ''
  const debugAvailability = {
    merchandisingCampaign: props.merchandisingCampaign,
    experimentKey: props.experimentKey,
    variationKey: props.variationKey,
    decision: props.decision,
  }

  return (
    <ContentfulContextProvider space={props.contentfulMetadata.spaceId}>
      <ContentfulLink id={props.contentfulMetadata?.contentful_id} />
      <DebugSection contentfulMetadata={props.contentfulMetadata} availability={debugAvailability} search={search} />
      <SpacingWrapper>
        {bannerType ? <PillarSlimBanner pillarData={pillarData} carouselA11yText={props.carouselA11yText} /> : <PromoSlimBanner {...promoData} />}
      </SpacingWrapper>
    </ContentfulContextProvider>
  )
}

function trackUsedContext(props: any) {
  const tracker = useUserContextTracker()
  if (props.experimentKey) {
    tracker({
      type: 'experiment',
      name: props.experimentKey,
      variation: props.variationKey,
    })
  }
  if (props.decision) {
    tracker({
      type: 'treatment',
      name: props.decision,
    })
  }
  if (props.merchandisingCampaign) {
    tracker({
      type: 'campaign',
      name: props.merchandisingCampaign,
    })
  }
}
